import { useEffect, useRef } from 'react';

const TERMS_CONTENT = {
  title: 'Terms of Service',
  sections: [
    {
      heading: 'Acceptance of Terms',
      body: 'By creating an account or using Inveira ("the Service"), you agree to these Terms of Service. If you do not agree, do not use the Service. Inveira is operated by Inveira Inc. These terms apply to all users, including students, parents, and school counselors.'
    },
    {
      heading: 'Account Usage',
      body: 'You must be at least 13 years old to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You agree not to share accounts, impersonate others, or use the Service for any unlawful purpose. We reserve the right to suspend accounts that violate these terms.'
    },
    {
      heading: 'No Guarantee of Admission Results',
      body: 'Inveira is a practice and preparation tool. Nothing in the Service constitutes a guarantee, warranty, or representation that using it will result in college admission, scholarship awards, or any other outcome. Interview feedback and coaching are provided for educational purposes only. Admissions decisions are made solely by colleges and universities.'
    },
    {
      heading: 'User Data and Content',
      body: 'You retain ownership of content you submit, including essays and interview recordings. By using the Service, you grant Inveira a limited license to process your content solely to provide the features you use (e.g., AI analysis, feedback generation). We do not sell your personal data or use your content to train AI models without your explicit consent.'
    },
    {
      heading: 'Data Sharing for Research & Analytics',
      body: 'With your optional consent, we may collect and share anonymized performance data, interview responses, and academic profile information with third-party research and analytics partners to improve educational outcomes. This data is always de-identified before sharing — your name, email, and any directly identifying information are removed. Passwords, payment information, and government IDs are never shared under any circumstances. You can opt in to this program during signup or opt out at any time by contacting somaanfater@gmail.com.'
    },
    {
      heading: 'Payment Terms',
      body: 'Free plan users receive one practice interview per month at no charge. Paid subscriptions are billed monthly or annually as selected at checkout. All payments are processed via Stripe. Subscriptions auto-renew unless canceled before the renewal date. Refunds are available within 7 days of purchase if no interviews have been used in the billing period. School plan pricing is negotiated separately.'
    },
    {
      heading: 'Prohibited Conduct',
      body: 'You may not: (a) attempt to reverse-engineer, scrape, or copy the Service; (b) submit false or misleading information; (c) use the Service to generate content for another person\'s application without disclosure; (d) interfere with the security or integrity of the platform. Violations may result in immediate account termination without refund.'
    },
    {
      heading: 'Termination',
      body: 'Either party may terminate the relationship at any time. You may delete your account from Settings. We may suspend or terminate your account for violations of these terms, nonpayment, or at our discretion with 30 days notice. Upon termination, your data will be deleted within 90 days unless legally required to retain it.'
    },
    {
      heading: 'Limitation of Liability',
      body: 'To the fullest extent permitted by law, Inveira is not liable for any indirect, incidental, or consequential damages arising from your use of the Service. Our total liability for any claim is limited to the amount you paid us in the 3 months preceding the claim.'
    },
    {
      heading: 'Changes to Terms',
      body: 'We may update these Terms from time to time. We will notify you by email or in-app notice at least 14 days before material changes take effect. Continued use of the Service after changes constitutes acceptance.'
    },
    {
      heading: 'Contact',
      body: 'Questions about these Terms? Email us at somaanfater@gmail.com.'
    }
  ]
};

const PRIVACY_CONTENT = {
  title: 'Privacy Policy',
  sections: [
    {
      heading: 'What We Collect',
      body: 'We collect the following categories of information: (1) Account information: your name, email address, and password hash. (2) Profile information: academic data you enter (GPA, test scores, activities, essays) to personalize your practice. (3) Interview data: audio recordings, transcripts, and video frames captured during practice interviews. (4) Usage data: pages visited, features used, and timestamps, collected via standard server logs.'
    },
    {
      heading: 'How We Use Your Data',
      body: 'Your data is used exclusively to provide the Service: generating interview questions, analyzing your responses, producing feedback reports, and improving your experience across sessions. We do not use your personal information or interview content to train AI models without your explicit, opt-in consent. We do not sell, rent, or trade your personal data to third parties for marketing purposes.'
    },
    {
      heading: 'Data Sharing for Research & Analytics',
      body: 'With your optional consent, we may collect and share anonymized performance data, interview responses, and academic profile information with third-party research and analytics partners to improve educational outcomes. Anonymization means your name, email address, and all directly identifying information are removed before any data leaves our systems. Passwords, payment information, and government IDs are never shared under any circumstances. You can opt in during signup or opt out at any time by contacting somaanfater@gmail.com. Withdrawing consent does not affect data already shared in anonymized form.'
    },
    {
      heading: 'Third-Party Services',
      body: 'To deliver the Service, we share data with the following sub-processors under data processing agreements: (1) Anthropic — AI language model provider used for interview question generation and answer analysis. (2) OpenAI — Whisper API used for audio transcription of your interview answers. (3) HeyGen — AI avatar video generation for interviewer simulations. (4) Neon / PostgreSQL — cloud database hosting your account and interview data. (5) Stripe — payment processing (we never see your full card number). (6) SendGrid — transactional email delivery. Each provider is bound to use your data only as directed by us.'
    },
    {
      heading: 'Interview Recordings',
      body: 'Audio and video captured during interviews is processed in real time for transcription and analysis. Recordings are stored encrypted and are accessible only to you and authorized Inveira staff for support purposes. You may request deletion of specific recordings at any time from your Settings page.'
    },
    {
      heading: 'Data Retention',
      body: 'We retain your account and profile data for as long as your account is active. Interview reports are retained for 2 years so you can track progress over time. You may request full deletion of your account and all associated data by emailing somaanfater@gmail.com. We will complete deletion within 30 days and confirm via email.'
    },
    {
      heading: 'COPPA Notice — Users Under 13',
      body: 'Inveira is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child under 13 has created an account, please contact us at somaanfater@gmail.com immediately and we will delete the account and all associated data.'
    },
    {
      heading: 'Users 13–17',
      body: 'If you are between 13 and 17, please review these terms with a parent or guardian before creating an account. Parents of minor users may contact us to review, update, or delete their child\'s data.'
    },
    {
      heading: 'Cookies and Tracking',
      body: 'We use only essential cookies required for authentication and session management. We do not use advertising cookies or cross-site tracking. You can disable cookies in your browser settings, though this will prevent you from staying logged in.'
    },
    {
      heading: 'Security',
      body: 'We implement industry-standard security practices including encrypted data transmission (TLS), hashed passwords (bcrypt), and access controls. No method of transmission over the internet is 100% secure; we cannot guarantee absolute security but commit to prompt disclosure and remediation of any breaches.'
    },
    {
      heading: 'Your Rights',
      body: 'Depending on your location, you may have rights to access, correct, or delete your personal data, or to object to certain processing. To exercise these rights, email somaanfater@gmail.com. We will respond within 30 days.'
    },
    {
      heading: 'Contact',
      body: 'Privacy questions or requests: somaanfater@gmail.com. Mailing address: Inveira Inc., [Address on file].'
    }
  ]
};

export const POLICIES = {
  terms: TERMS_CONTENT,
  privacy: PRIVACY_CONTENT,
};

export default function PolicyModal({ type, onClose }) {
  const policy = POLICIES[type];
  const scrollRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    // Focus the scroll region so keyboard scrolling (PageDown/arrows/space)
    // targets it — body is overflow:hidden, so nothing else would scroll.
    scrollRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!policy) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{policy.title}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable content. data-lenis-prevent stops Lenis (active on the
            landing page) from hijacking wheel/trackpad here; tabIndex makes it
            keyboard-scrollable. */}
        <div
          ref={scrollRef}
          data-lenis-prevent
          tabIndex={0}
          className="overflow-y-auto px-6 py-5 space-y-5 focus:outline-none"
        >
          {policy.sections.map((section) => (
            <div key={section.heading}>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">{section.heading}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{section.body}</p>
            </div>
          ))}
          <p className="text-xs text-gray-400 pt-2">Last updated: May 2025</p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
